import classNames from "classnames";
import { useTranslation } from "react-i18next";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";

import { useEventTypeLabel } from "shared/locale/hooks";

import VINLink from "pages/Dashboard/components/VINLink";
import { LandingPageViewConfig } from "pages/shared/Dashboard/types";
import { getAttributeValue } from "pages/shared/schemaDefinitions/utils";

import Button from "features/components/Button";
import { SchemaEntry } from "features/ui/MaterialTable";

interface EventDetailsHeaderProps {
  config: LandingPageViewConfig;
  event: {
    VIN: string;
    [key: string]: any;
  };
  dateField: string;
  onNext?: () => void;
  onPrevious?: () => void;
  currentIndex?: number;
  count?: number;
  getDisplayLabel: (accessor: string, fallback: string) => string;
  getSchemaForAccessor: (accessor: string) => SchemaEntry<string> | undefined;
  smartGroupName?: string;
  isEventDetailPage?: boolean;
}

const EventDetailsHeader = ({
  config,
  event,
  dateField,
  onNext,
  onPrevious,
  currentIndex,
  count,
  getDisplayLabel,
  getSchemaForAccessor,
  smartGroupName,
  isEventDetailPage,
}: EventDetailsHeaderProps) => {
  const { eventType } = config;
  const { t } = useTranslation();

  const eventTypeLabel = useEventTypeLabel(eventType);
  const defaultDateLabel = t("schema:dashboard.event_type_date", {
    eventType: eventTypeLabel,
  });
  const eventTypeLabelLowercase = config.titleSingular
    ? config.titleSingular.toLowerCase()
    : eventTypeLabel.toLowerCase();

  return (
    <div
      className={classNames("flex flex-row p-4 bg-slate-100", {
        "pt-1": isEventDetailPage,
      })}
    >
      <div className="flex flex-row gap-4 items-center">
        <div className="px-4 py-2">
          <div className="font-bold">
            {getDisplayLabel(dateField, defaultDateLabel)}
          </div>
          <div>
            {getAttributeValue(dateField, event, getSchemaForAccessor, t)}
          </div>
        </div>
        <div>
          <div className="font-bold">
            {getDisplayLabel("VIN", t("common:vin"))}
          </div>
          <div className="underline">
            <VINLink config={config} vin={event.VIN} />
          </div>
        </div>
      </div>
      <div className="flex flex-row gap-2 items-center ml-auto">
        {(count || smartGroupName) && (
          <div className="flex flex-row gap-4 items-center mr-4">
            <div className="text-lg">
              {currentIndex}/{count}
            </div>
            {smartGroupName && (
              <div>
                <div className="font-bold">
                  {t("schema:dashboard.smart_group_label")}
                </div>
                <div>{smartGroupName}</div>
              </div>
            )}
          </div>
        )}
        {onPrevious && (
          <Button
            color="secondary"
            label={t("schema:dashboard.previous_item", {
              item: eventTypeLabelLowercase,
            })}
            variant="contained"
            onClick={onPrevious}
            startIcon={<ExpandMoreOutlinedIcon className="rotate-90" />}
          />
        )}
        {onNext && (
          <Button
            color="secondary"
            variant="contained"
            label={t("schema:dashboard.next_item", {
              item: eventTypeLabelLowercase,
            })}
            onClick={onNext}
            endIcon={<ExpandMoreOutlinedIcon className="rotate-270" />}
          />
        )}
      </div>
    </div>
  );
};

export default EventDetailsHeader;
